"use client";

import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const OrganizationChartContext = createContext(null);

export const OrganizationChartProvider = ({ children }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getToken = () => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("token");
    }
    return null;
  };

  const fetchOrganizationChart = useCallback(async () => {
    const token = getToken();

    if (!token) {
      setError("Authentication token missing");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${BASE_URL}/organization-chart`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok || !data.status) {
        throw new Error(
          data.message || "Failed to fetch organization chart"
        );
      }

      setEmployees(data.data || []);
    } catch (error) {
      console.error("Organization chart error:", error);

      const message =
        error.message || "Error fetching organization chart";

      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganizationChart();
  }, [fetchOrganizationChart]);

  return (
    <OrganizationChartContext.Provider
      value={{
        employees,
        loading,
        error,
        fetchOrganizationChart,
        setEmployees,
      }}
    >
      {children}
    </OrganizationChartContext.Provider>
  );
};

export const useOrganizationChart = () => {
  const context = useContext(OrganizationChartContext);

  if (!context) {
    throw new Error(
      "useOrganizationChart must be used inside OrganizationChartProvider"
    );
  }

  return context;
};